import { Fragment, ReactNode, useState } from "react"
import { Spinner } from "reactstrap"
import { KpiRow } from "models/financial-kpi"
import { ChevronDownIcon, ChevronRightIcon } from "Components/atoms/icons"
import { FISCAL_MONTHS, formatKpiValue, formatPct } from "./format"

export interface KpiTableGroup {
    key: string
    title?: string
    rows: KpiRow[]
}

export interface KpiTableSection {
    key: string
    title: string
    subtitle?: ReactNode
    actions?: ReactNode
    isLoading: boolean
    groups: KpiTableGroup[]
    emptyMessage?: string
}

// period: 1 (September) ... 12 (August), or "ytd"
export type KpiCellPeriod = number | "ytd"

// Columns of the table. Months have no width: they share what is left, so the table fits the page
// (no horizontal scroll, which would also break the sticky header).
export interface KpiColumn {
    id: string
    label: string
    width?: number
    canHide?: boolean
}

export const KPI_COLUMNS: KpiColumn[] = [
    { id: "name", label: "KPI", width: 210, canHide: false },
    { id: "position", label: "Position", width: 105 },
    { id: "owner", label: "Owner", width: 115 },
    { id: "target", label: "Target", width: 95 },
    ...FISCAL_MONTHS.map((m, i) => ({ id: `m${i + 1}`, label: m })),
    { id: "ytd", label: "YTD", width: 100 },
    { id: "pct", label: "% Target", width: 70 },
]

const FIXED_COLUMNS = new Set(["name", "position", "owner", "target"])

interface KpiMasterTableProps {
    sections: KpiTableSection[]
    hiddenColumns?: Set<string>
    onCellClick?: (row: KpiRow, period: KpiCellPeriod) => void
}

// Finance > Financial KPI Metrics: every container in one table. Same look as the shared DataTable (data-table,
// tree-name-cell, is-group-row) plus the module / group title rows of the Gross Margin report; extras in
// _financial-kpi.scss. A single sticky header, module title rows (collapsible), optional group title rows, then
// the KPI rows. A row is a parent when the next row in its group is deeper; parents collapse their children.
// Month and YTD values open the detail. Month cells from a saved snapshot are marked "S", live values "L".
const KpiMasterTable = ({ sections, hiddenColumns, onCellClick }: KpiMasterTableProps) => {
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
    const [collapsedRows, setCollapsedRows] = useState<Set<string>>(new Set())

    const columns = KPI_COLUMNS.filter(c => !hiddenColumns?.has(c.id))
    const columnCount = columns.length

    const toggle = (setter: typeof setCollapsedRows) => (key: string) => setter(prev => {
        const next = new Set(prev)
        next.has(key) ? next.delete(key) : next.add(key)
        return next
    })
    const toggleSection = toggle(setCollapsedSections)
    const toggleRow = toggle(setCollapsedRows)

    const valueCell = (row: KpiRow, value: number | null, period: KpiCellPeriod, extra?: ReactNode, title?: string) => {
        const clickable = value !== null && !!onCellClick
        const classes = ["fkpi-number", value !== null && value < 0 ? "fkpi-negative" : "", clickable ? "fkpi-clickable" : ""].join(" ")
        return (
            <td key={String(period)} className={classes} title={title ?? formatKpiValue(value, row.unit)}
                onClick={clickable ? () => onCellClick!(row, period) : undefined}>
                {formatKpiValue(value, row.unit)}{extra}
            </td>
        )
    }

    const cell = (column: KpiColumn, row: KpiRow, isParent: boolean, isCollapsed: boolean): ReactNode => {
        switch (column.id) {
            case "name":
                return (
                    <td key="name" className="fkpi-fixed fkpi-name">
                        <span className={`tree-name-cell ${isParent ? "is-group" : ""}`}
                            style={{ paddingLeft: row.depth * 20 }}
                            role={isParent ? "button" : undefined}
                            title={row.label}
                            onClick={isParent ? () => toggleRow(row.key) : undefined}>
                            {isParent
                                ? (isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />)
                                : row.depth > 0 && <span className="tree-name-cell__spacer" />}
                            <span className="tree-name-cell__text">{row.label}</span>
                        </span>
                    </td>
                )
            case "position":
                return <td key="position" className="fkpi-fixed" title={row.position ?? undefined}>{row.position ?? <span className="text-muted">—</span>}</td>
            case "owner":
                return <td key="owner" className="fkpi-fixed" title={row.owner ?? undefined}>{row.owner ?? <span className="text-muted">—</span>}</td>
            case "target":
                return <td key="target" className="fkpi-fixed fkpi-number">{formatKpiValue(row.target, row.unit)}</td>
            case "ytd":
                return valueCell(row, row.ytd, "ytd")
            case "pct":
                return <td key="pct" className="fkpi-number">{formatPct(row.pct)}</td>
            default: {
                const m = Number(column.id.slice(1)) - 1
                const value = row.months[m]
                const source = row.sources?.[m]
                const marker = value !== null && source && source !== "actual"
                    ? <sup className="ms-1 text-muted">{source === "snapshot" ? "S" : "L"}</sup>
                    : undefined
                const title = source === "snapshot" ? "Saved snapshot" : source === "live" ? "Live (no snapshot for this month)" : undefined
                return valueCell(row, value, m + 1, marker, title)
            }
        }
    }

    const renderRows = (rows: KpiRow[]) => {
        const out: ReactNode[] = []
        let hideBelow: number | null = null
        rows.forEach((row, i) => {
            if (hideBelow !== null && row.depth > hideBelow) return
            hideBelow = null
            const isParent = i + 1 < rows.length && rows[i + 1].depth > row.depth
            const isCollapsed = collapsedRows.has(row.key)
            if (isParent && isCollapsed) hideBelow = row.depth

            out.push(
                <tr key={row.key} className={`fkpi-row ${isParent ? "is-group-row" : ""}`}>
                    {columns.map(c => cell(c, row, isParent, isCollapsed))}
                </tr>
            )
        })
        return out
    }

    return (
        <div className="fkpi-scroll">
            <table className="data-table table align-middle fkpi-table" style={{ tableLayout: "fixed", width: "100%" }}>
                <colgroup>{columns.map(c => <col key={c.id} style={c.width ? { width: c.width } : undefined} />)}</colgroup>
                <thead>
                    <tr>
                        {columns.map(c => (
                            <th key={c.id} title={c.label}
                                className={c.id === "name" ? "fkpi-name" : FIXED_COLUMNS.has(c.id) && c.id !== "target" ? "" : "text-end"}>
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sections.map(section => {
                        const isCollapsed = collapsedSections.has(section.key)
                        const isEmpty = section.groups.every(g => g.rows.length === 0)
                        return (
                            <Fragment key={section.key}>
                                <tr className="fkpi-section">
                                    <td colSpan={columnCount}>
                                        <div className="fkpi-title">
                                            <span className="d-inline-flex align-items-center gap-1" style={{ cursor: "pointer", fontSize: 14 }}
                                                onClick={() => toggleSection(section.key)}>
                                                {isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
                                                {section.title}
                                            </span>
                                            {section.subtitle && <small className="text-muted fw-normal">{section.subtitle}</small>}
                                            {section.actions}
                                        </div>
                                    </td>
                                </tr>
                                {!isCollapsed && (section.isLoading
                                    ? <tr><td colSpan={columnCount}><div className="fkpi-title py-2"><Spinner size="sm" /> Loading...</div></td></tr>
                                    : isEmpty
                                        ? <tr><td colSpan={columnCount}><div className="fkpi-title py-2 text-muted">{section.emptyMessage ?? "No data for this fiscal year."}</div></td></tr>
                                        : section.groups.map(group => (
                                            <Fragment key={group.key}>
                                                {group.title &&
                                                    <tr className="fkpi-group">
                                                        <td colSpan={columnCount}>
                                                            <div className="fkpi-title" style={{ left: 16 }}>{group.title}</div>
                                                        </td>
                                                    </tr>}
                                                {renderRows(group.rows)}
                                            </Fragment>
                                        )))}
                            </Fragment>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default KpiMasterTable
